<template>
  <app-page labelledby="finance-overview-title" full-width>
    <page-header
      title-id="finance-overview-title"
      title="Обзор финансов"
      description="Сводные показатели и последние бухгалтерские события."
    >
      <template #status>
        <status-badge :label="freshness.label" :tone="freshness.tone" :icon="freshness.icon" data-testid="finance-overview-freshness" />
      </template>
      <template #metadata>
        <div class="overview-context">
          <span><strong>Период:</strong> {{ year }} год</span>
          <span><strong>Обновлено:</strong> {{ calculatedAtLabel }}</span>
        </div>
      </template>
      <template #actions>
        <q-select v-model="year" dense outlined :options="yearOptions" label="Год" class="year-select" @update:model-value="loadSummary" />
      </template>
    </page-header>

    <page-section title="Финансовые результаты" description="Показатели отображаются по мере готовности данных.">
      <app-state
        v-if="summaryLoading"
        state="loading"
        title="Загружаем финансовую сводку"
        message="Получаем показатели."
        data-testid="finance-overview-loading"
      />
      <app-state
        v-else-if="summaryError"
        state="error"
        title="Финансовая сводка недоступна"
        :message="summaryError"
        @retry="loadSummary"
      />
      <div v-else>
        <div v-if="summary.isStale" class="stale-notice" role="status" data-testid="finance-overview-stale">
          <q-icon name="warning_amber" aria-hidden="true" />
          <span>Показатели устарели. Для актуальных значений сформируйте их на главной странице организации.</span>
        </div>
        <div class="finance-kpis" data-testid="finance-overview-kpis">
          <article v-for="card in kpiCards" :key="card.key" class="finance-kpi" :data-kpi="card.key">
            <span>{{ card.label }}</span>
            <amount-cell v-if="card.available" :value="card.value" currency="RUB" />
            <strong v-else class="finance-kpi__missing">—</strong>
            <small>{{ card.available ? 'По данным учета' : 'Данные не предоставлены' }}</small>
          </article>
        </div>
      </div>
    </page-section>

    <div class="overview-columns">
      <page-section title="Ближайшие события" description="Сроки и действия бухгалтерского периода.">
        <div class="events-empty" role="status" data-testid="finance-events-empty">
          <q-icon name="event_available" aria-hidden="true" />
          <div>
            <strong>Ближайших событий не найдено</strong>
            <span>Настроенные события появятся в этом разделе.</span>
          </div>
        </div>
      </page-section>

      <page-section title="Диагностика" description="Пояснения к показателям.">
        <ul v-if="summary.diagnostics.length" class="diagnostics" data-testid="finance-diagnostics">
          <li v-for="(item, index) in summary.diagnostics" :key="item.code || index">{{ item.message || item.code || 'Диагностическое сообщение' }}</li>
        </ul>
        <div v-else class="events-empty" role="status">
          <q-icon name="verified" aria-hidden="true" />
          <div><strong>Диагностических сообщений нет</strong><span>Проблем не обнаружено.</span></div>
        </div>
      </page-section>
    </div>

    <page-section title="Недавние документы" description="Последние документы по доступным типам.">
      <app-data-table
        title="Документы"
        :count="documents.length"
        :rows="documents"
        :columns="documentColumns"
        :pagination="pagination"
        :state="documentsState"
        :state-title="documentsStateTitle"
        :state-message="documentsError || 'Документы появятся после создания.'"
        storage-key="finance-overview-documents"
        data-testid="finance-recent-documents"
        @retry="loadDocuments"
      >
        <template #body-cell-date="props"><q-td :props="props"><date-cell :value="props.row.date" /></q-td></template>
        <template #body-cell-is_in_nds="props">
          <q-td :props="props"><status-badge :label="props.value ? 'Да' : 'Нет'" :tone="props.value ? 'success' : 'neutral'" :icon="props.value ? 'check' : 'remove'" /></q-td>
        </template>
        <template #body-cell-actions="props">
          <q-td :props="props" class="text-right">
            <q-btn v-if="documentRoute(props.row)" flat no-caps color="primary" label="Открыть" :to="documentRoute(props.row)" />
          </q-td>
        </template>
      </app-data-table>
    </page-section>
  </app-page>
</template>

<script>
import moment from 'moment'
import AmountCell from '../../components/ui/AmountCell.vue'
import AppDataTable from '../../components/ui/AppDataTable.vue'
import AppPage from '../../components/ui/AppPage.vue'
import AppState from '../../components/ui/AppState.vue'
import DateCell from '../../components/ui/DateCell.vue'
import PageHeader from '../../components/ui/PageHeader.vue'
import PageSection from '../../components/ui/PageSection.vue'
import StatusBadge from '../../components/ui/StatusBadge.vue'
import { formatDateTime } from '../../services/dateFormat'
import { documentRoute, normalizeDocumentsResponse, normalizeFinanceSummary } from '../../services/financeCore'

const documentColumns = Object.freeze([
  { name: 'in_org_number', label: 'Номер', field: 'in_org_number', align: 'left', required: true, sortable: true },
  { name: 'name', label: 'Документ', field: 'name', align: 'left', required: true },
  { name: 'user', label: 'Автор', field: 'user', align: 'left', classes: 'column-priority-secondary' },
  { name: 'direction', label: 'Направление', field: 'direction_display', align: 'left' },
  { name: 'date', label: 'Дата', field: 'date', align: 'left', sortable: true },
  { name: 'is_in_nds', label: 'В НДС', field: 'is_in_nds', align: 'left', classes: 'column-priority-secondary' },
  { name: 'actions', label: 'Действия', field: 'actions', align: 'right', required: true }
])

export default {
  name: 'FinanceOverview',
  components: { AmountCell, AppDataTable, AppPage, AppState, DateCell, PageHeader, PageSection, StatusBadge },
  data() {
    const year = Number(moment().format('YYYY'))
    return {
      year,
      yearOptions: Array.from({ length: 5 }, (_, index) => year - index),
      summary: { cards: {}, diagnostics: [], isStale: false, calculatedAt: null },
      summaryLoading: true,
      summaryError: '',
      documents: [],
      documentsLoading: true,
      documentsError: '',
      documentColumns,
      pagination: { page: 1, rowsPerPage: 10, sortBy: 'date', descending: true }
    }
  },
  computed: {
    kpiCards() {
      const definitions = [
        { key: 'assets_total', label: 'Активы всего', aliases: ['assets_total', 'assets'] },
        { key: 'liabilities_total', label: 'Пассивы всего', aliases: ['liabilities_total', 'liabilities'] },
        { key: 'vat_payable', label: 'НДС к уплате', aliases: ['vat_payable', 'vat_total', 'nds_total'] }
      ]
      return definitions.map((card) => {
        const key = card.aliases.find((alias) => Object.prototype.hasOwnProperty.call(this.summary.cards, alias))
        return { ...card, available: Boolean(key), value: key ? this.summary.cards[key] : null }
      })
    },
    calculatedAtLabel() {
      return this.summary.calculatedAt ? formatDateTime(this.summary.calculatedAt, 'неизвестно') : 'неизвестно'
    },
    freshness() {
      if (this.summaryLoading) return { label: 'Обновляем', tone: 'info', icon: 'sync' }
      if (this.summaryError) return { label: 'Недоступно', tone: 'danger', icon: 'error_outline' }
      if (this.summary.isStale) return { label: 'Требует формирования', tone: 'warning', icon: 'warning_amber' }
      return { label: 'Актуально', tone: 'success', icon: 'verified' }
    },
    documentsState() {
      if (this.documentsLoading) return 'loading'
      if (this.documentsError) return 'error'
      return this.documents.length ? 'ready' : 'empty'
    },
    documentsStateTitle() {
      return this.documentsError ? 'Документы недоступны' : 'Недавних документов нет'
    }
  },
  created() {
    this.emitter.on('onOrganisationFinanceSummary', this.onSummary)
    this.emitter.on('didReceiveDocumentsList', this.onDocuments)
    this.loadSummary()
    this.loadDocuments()
  },
  beforeUnmount() {
    this.emitter.off('onOrganisationFinanceSummary', this.onSummary)
    this.emitter.off('didReceiveDocumentsList', this.onDocuments)
  },
  methods: {
    documentRoute,
    whenReady(request) {
      if (this.connection.isAuthenticated) request()
      else {
        const ready = () => {
          this.emitter.off('connectionReady', ready)
          request()
        }
        this.emitter.on('connectionReady', ready)
      }
    },
    loadSummary() {
      this.summaryLoading = true
      this.summaryError = ''
      this.whenReady(() => this.connection.getOrganisationFinanceSummary(`finance-overview-${this.year}`, this.year))
    },
    loadDocuments() {
      this.documentsLoading = true
      this.documentsError = ''
      this.whenReady(() => this.connection.getDocumentsList('finance-overview-documents', 0, moment().endOf('day').unix(), 'all', 'upd,payment,avance,act_sver', true, 10))
    },
    onSummary(response) {
      const result = normalizeFinanceSummary(response)
      this.summaryLoading = false
      if (!result.ok) {
        this.summaryError = result.message
        return
      }
      this.summary = result
    },
    onDocuments(response) {
      const result = normalizeDocumentsResponse(response)
      this.documentsLoading = false
      if (!result.ok) {
        this.documents = []
        this.documentsError = result.message
        return
      }
      this.documents = result.rows
    }
  }
}
</script>

<style scoped>
.overview-context { display: flex; flex-wrap: wrap; gap: var(--sid-space-4); }
.year-select { width: 8rem; }
.finance-kpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); }
.finance-kpi { display: flex; min-height: 9rem; flex-direction: column; justify-content: space-between; padding: var(--sid-space-4); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-large); background: var(--sid-color-surface-0); }
.finance-kpi > span, .finance-kpi > small { color: var(--sid-color-text-secondary); }
.finance-kpi :deep(.amount-cell), .finance-kpi__missing { margin: var(--sid-space-3) 0; text-align: left; font-size: clamp(1.5rem, 2vw, 2rem); font-weight: var(--sid-font-weight-semibold); }
.stale-notice { display: flex; align-items: center; gap: var(--sid-space-2); margin-bottom: var(--sid-space-4); padding: var(--sid-space-3); border-radius: var(--sid-radius-medium); background: var(--sid-color-warning-surface); color: var(--sid-color-warning); }
.overview-columns { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); margin-top: var(--sid-space-6); padding-top: var(--sid-space-6); border-top: 1px solid var(--sid-color-line); }
.overview-columns :deep(.page-section) { margin: 0; padding: 0; border: 0; }
.events-empty { display: flex; min-height: 7rem; align-items: center; gap: var(--sid-space-3); padding: var(--sid-space-4); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-large); background: var(--sid-color-surface-subtle); }
.events-empty .q-icon { font-size: 2rem; color: var(--sid-color-text-secondary); }
.events-empty strong, .events-empty span { display: block; }
.events-empty span { margin-top: var(--sid-space-1); color: var(--sid-color-text-secondary); }
.diagnostics { margin: 0; padding: var(--sid-space-4) var(--sid-space-6); border-radius: var(--sid-radius-large); background: var(--sid-color-warning-surface); }
@media (max-width: 767px) {
  .finance-kpis, .overview-columns { grid-template-columns: 1fr; }
  .year-select { width: 100%; }
}
</style>
